import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Trophy, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";

const fmt = (v, unit, decimals) => (v == null ? "—" : `${v.toLocaleString("nb-NO", { maximumFractionDigits: decimals ?? (unit ? 1 : 2) })}${unit ? " %" : ""}`);

const rankColor = (rank, count) => {
  const q = rank / count;
  if (q <= 0.34) return "text-[#2F7D5B]";
  if (q <= 0.67) return "text-[#B4791F]";
  return "text-[#C0392B]";
};

const MetricBlock = ({ m, kommuneName, gruppeOrd, snittLabel }) => {
  const [open, setOpen] = useState(false);
  const vals = m.kommuner.map((k) => Math.abs(k.value));
  const max = Math.max(...vals, 0.0001);
  const min = Math.min(...vals, Infinity);
  const barWidth = (v) => (m.higher_is_better ? (Math.abs(v) / max) * 100 : (min / Math.max(Math.abs(v), 0.0001)) * 100);
  const list = open ? m.kommuner : m.kommuner.filter((k) => k.rank <= 3 || Math.abs(k.rank - m.rank) <= 1 || k.rank === m.count);
  return (
    <div className="border border-[#E6E9F0] rounded-lg p-4" data-testid={`rangering-${m.key}`}>
      <div className="flex items-start gap-2">
        <p className="text-sm font-medium text-[#1F2A36] flex-1">{m.label}</p>
      </div>
      <p className="text-xs text-[#8AA4BF] mt-0.5 mb-3">{m.note}</p>
      <div className="flex items-end gap-4 mb-3">
        <div>
          <p className={`text-3xl font-light ${m.only_land ? "text-[#1F2A36]" : rankColor(m.rank, m.count)}`} data-testid={`rangering-${m.key}-plass`}>
            {m.only_land ? m.land_rank : m.rank}. <span className="text-base text-[#4B647F]">av {m.only_land ? m.land_count : m.count}</span>
          </p>
          <p className="text-xs text-[#4B647F]">
            {m.only_land ? "i landet " : `i ${gruppeOrd} `}i {m.year} · {fmt(m.value, m.unit, m.decimals)}
          </p>
        </div>
        <div className="text-xs text-[#4B647F] ml-auto text-right">
          {!m.only_land && <p>{snittLabel}: {fmt(m.gruppe_avg ?? m.fylke_avg, m.unit, m.decimals)}</p>}
          <p>Landssnitt: {fmt(m.land_avg, m.unit, m.decimals)}</p>
          <p className="text-[#8AA4BF]">
            {m.only_land ? "Eigen kommune utgjer heile fylket" : `Nr. ${m.land_rank} av ${m.land_count} i landet`}
          </p>
        </div>
      </div>
      <div className="space-y-1">
        {list.map((k) => {
          const self = k.name === kommuneName;
          return (
            <div key={k.kommune_code} className="flex items-center gap-2 text-xs">
              <span className="w-6 text-right text-[#8AA4BF]">{k.rank}.</span>
              <span className={`w-28 truncate ${self ? "font-semibold text-[#103766]" : "text-[#4B647F]"}`}>{k.name}</span>
              <span className="flex-1 h-2.5 bg-[#F0F3F8] rounded-full overflow-hidden">
                <span
                  className={`block h-full rounded-full transition-all duration-500 ${self ? "bg-[#103766]" : "bg-[#9FB6CC]"}`}
                  style={{ width: `${Math.max(2, Math.min(100, barWidth(k.value)))}%` }}
                />
              </span>
              <span className={`w-16 text-right ${self ? "font-semibold text-[#103766]" : "text-[#4B647F]"}`}>{fmt(k.value, m.unit, m.decimals)}</span>
            </div>
          );
        })}
      </div>
      {m.kommuner.length > list.length || open ? (
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="mt-3 inline-flex items-center gap-1 text-xs text-[#103766] hover:underline"
          data-testid={`rangering-${m.key}-toggle`}
        >
          {open ? <>Vis færre <ChevronUp className="w-3 h-3" /></> : <>Vis alle {m.count} kommunar <ChevronDown className="w-3 h-3" /></>}
        </button>
      ) : null}
    </div>
  );
};

export const RangeringSection = ({ data }) => {
  if (!data?.metrics?.length) return null;
  const onlyLand = data.metrics.every((m) => m.only_land);
  const storby = !!data.gruppe_namn;
  const gruppeOrd = storby ? "storbygruppa" : "fylket";
  const snittLabel = storby ? "Snitt for storbyane" : "Fylkessnitt";
  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-8" data-testid="rangering-card">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
          <Trophy className="w-5 h-5 text-[#103766]" />
          {onlyLand ? "Rangering i landet" : storby ? "Rangering blant storbyane" : `Rangering i ${data.fylke_name || "fylket"}`}
        </CardTitle>
        <p className="text-sm text-[#4B647F]">
          Kor {data.kommune_name} rangerer blant {onlyLand ? "alle landets kommunar" : storby ? "Oslo, Bergen, Trondheim og Stavanger" : "fylkets kommunar"} på likviditetsgrad, arbeidskapital, gjeldsgrad og premieavvik.</p>
        <p className="text-sm text-[#4B647F]">{storby ? "Dei fire største kommunane er så ulike resten av fylket sitt at vi samanliknar dei med kvarandre i staden." : ""}
        </p>
      </CardHeader>
      <CardContent className="pt-6 space-y-4">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          {data.metrics.map((m) => (
            <MetricBlock key={m.key} m={m} kommuneName={data.kommune_name} gruppeOrd={gruppeOrd} snittLabel={snittLabel} />
          ))}
        </div>
        <p className="text-xs text-[#8AA4BF]">
          Rangeringa gjeld siste publiserte rekneskapsår og samanliknar balansetala i kommunekassa mot brutto driftsinntekter. Snitta er uvekta gjennomsnitt.
        </p>
        <p className="text-xs text-[#8AA4BF]">
          Kjelde: SSB <a href="https://www.ssb.no/statbank/table/13545" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">13545 <ExternalLink className="w-3 h-3" /></a> (balanse) og <a href="https://www.ssb.no/statbank/table/12858" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">12858 <ExternalLink className="w-3 h-3" /></a> (driftsinntekter).
        </p>
      </CardContent>
    </Card>
  );
};

export default RangeringSection;
